import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import useCopyToClipboard from '~/hooks/useCopyToClipboard';
import GlobalContainer from '~/components/global_container';

const CopyLink = ({
  activity = null,
  tab = null,
  message = null,
  leadId = null,
}) => {
  // Determine link destination - always link to Contact Profile (never to Inbox)
  const params = useMemo(() => {
    // No leadId means internal conversation - hide CopyLink
    if (!leadId) {
      return {};
    }

    // Activity from Contact Profile timeline
    if (activity) {
      return {
        lead:        leadId,
        _t:          Rails.helpers.currentTeam.id,
        activity_id: activity.id,
        ...(tab && tab !== 'all' && { tab }),
        ...(activity.internal && { internal: '1' }),
      };
    }

    // Inbox message with activity - link to Contact Profile timeline with activity scroll
    if (message && message.activity?.id) {
      const isInternal = message.activity?.internal;
      return {
        lead:        leadId,
        _t:          Rails.helpers.currentTeam.id,
        activity_id: message.activity.id,
        ...(isInternal && { internal: '1' }),
      };
    }

    // Fallback - just link to Contact Profile
    return {
      lead: leadId,
      _t:   Rails.helpers.currentTeam.id,
    };
  }, [activity, tab, message, leadId]);

  // Check if we have a valid Contact Profile link (has lead param)
  const isContactProfileLink = useMemo(() => Boolean(params.lead), [params]);

  const generateLink = useCallback(() => {
    // If no valid params, return empty string (CopyLink should be hidden)
    if (!isContactProfileLink) {
      return '';
    }

    const baseUrl = window.location.origin;
    const urlParams = new URLSearchParams(params);
    const queryString = urlParams.toString();

    // All links go to Contact Profile page (with or without activity_id for scrolling)
    const productPath = GlobalContainer.productUriPrefix() || '/recruiter';
    return `${baseUrl}${productPath}/today?${queryString}`;
  }, [params, isContactProfileLink]);

  const {
    copied,
    linkText,
    handleCopy,
    handleMouseLeave,
    handleKeyDown,
  } = useCopyToClipboard(generateLink);

  // Don't render CopyLink for internal conversations (no lead)
  if (!isContactProfileLink) {
    return null;
  }

  return (
    <CopyToClipboard text={linkText} onCopy={handleCopy}>
      <button
        type="button"
        className="copy-link text-muted mr-2"
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        title={copied ? 'Link copied!' : 'Copy link'}
      >
        {copied ? 'Copied!' : 'Copy link'}
      </button>
    </CopyToClipboard>
  );
};

CopyLink.propTypes = {
  activity: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  tab: PropTypes.string,
  message: PropTypes.shape({
    id:       PropTypes.string,
    activity: PropTypes.shape({
      id:       PropTypes.number,
      internal: PropTypes.bool,
    }),
  }),
  leadId: PropTypes.number,
};

export default CopyLink;
